import { useState } from 'react';
import {
    Box,
    ButtonGroup,
    Divider,
    Typography,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import MuiButton from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import {
    DeleteButton,
    EditButton,
    ShowButton,
    useRecordContext,
    Button,
    useRefresh,
    useUpdate,
    useNotify,
    useGetList,
    Confirm,
    useGetIdentity,
} from 'react-admin';
import {
    useCreatePath,
} from 'ra-core';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { quoteStatuses } from '@/root/defaultConfiguration';

import { useLocation } from 'react-router';
import { Quote, ProjectFile, QuotePriceHistoryEntry } from '@/types';
import { formatDate } from '@/utils/date_utils';
import { authedFetch, getAccessToken } from '@/providers/loginWithEmail/dataProvider';
import {
    CONTRACT_SIGNING_VOID_MESSAGE,
    isContractSigningActiveError,
} from '@/modules/projects/contract/contractSigningApi';
import { SendEstimateEmailDialog } from '@/modules/quotes/SendEstimateEmailDialog';
import { markQuoteEstimateSent } from '@/utils/quote_email_utils';
import { projectPhasePath } from '@/modules/global_search/phaseRoutes';
import { showAsideSx } from '@/components/layout/showPageLayout';

type PendingQuoteStatus = 'approved' | 'rejected';

const PRICE_CHANGE_REASON_MIN = 3;
const PRICE_CHANGE_REASON_MAX = 32;

function money(n: number): string {
    return `$${Number(n || 0).toLocaleString()}`;
}

function quoteStatusErrorMessage(error: unknown): string {
    const err = error as { body?: { errors?: Record<string, unknown>; detail?: unknown } };
    const errors = err?.body?.errors;
    const root = errors?.root;
    if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
        return String((root as { serverError: unknown }).serverError);
    }
    if (typeof root === 'string') return root;
    if (typeof err?.body?.detail === 'string') return err.body.detail;
    return 'Error on update quote';
}

function priceUpdateErrorMessage(error: unknown): string {
    const err = error as { body?: { errors?: Record<string, unknown>; detail?: unknown } };
    const errors = err?.body?.errors;
    const root = errors?.root;
    if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
        return String((root as { serverError: unknown }).serverError);
    }
    if (typeof root === 'string') return root;
    if (typeof err?.body?.detail === 'string') return err.body.detail;
    return 'Failed to update quote price';
}

export const QuoteAside = ({ link = 'edit' }: { link?: 'edit' | 'show' }) => {
    const location = useLocation();
    const record = useRecordContext<Quote>();
    if (!record) return null;

    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { identity } = useGetIdentity();
    const isSuperuser = Boolean(
        (identity as { administrator?: boolean } | undefined)?.administrator
    );

    const createPath = useCreatePath();
    const [isQuoteAccepting, setQuoteAccepting] = useState<boolean>(false);
    const [isRegenerating, setRegenerating] = useState<boolean>(false);
    const [voidSigningConfirmOpen, setVoidSigningConfirmOpen] = useState<boolean>(false);
    const [pendingStatus, setPendingStatus] = useState<PendingQuoteStatus | null>(null);
    const [sendEmailOpen, setSendEmailOpen] = useState(false);
    const [isMarkingSent, setMarkingSent] = useState(false);
    const [selectedPriceEntry, setSelectedPriceEntry] = useState<QuotePriceHistoryEntry | null>(null);
    const [priceEditOpen, setPriceEditOpen] = useState(false);
    const [priceEditValue, setPriceEditValue] = useState('');
    const [priceReason, setPriceReason] = useState('');
    const [priceEditError, setPriceEditError] = useState('');
    const [isSavingPrice, setSavingPrice] = useState(false);

    const priceHistoryNewestFirst = [...(record.price_history || [])].reverse();

    const { data: quoteDocuments } = useGetList<ProjectFile>('quotes/documents', {
        filter: { quote_id: record.id },
        pagination: { page: 1, perPage: 50 },
    });

    const hasEstimatorDocument =
        !!record.sharepoint_folder_name ||
        (quoteDocuments ?? []).some(
            doc => doc.additional_data?.source === 'estimator'
        );

    const estimateSentAt = (record.informative_status || []).find(
        s => s.status === 'is_estimate_sent'
    )?.date;

    const isLegacy = !!record.is_legacy;
    const projectPhase = record.project?.phase;
    const canApproveDeny =
        record.status === quoteStatuses.pending
        && (!isLegacy || projectPhase === 'quoting');
    const backTo = record.project_id
        ? projectPhasePath(record.project_id, projectPhase)
        : createPath({ resource: 'quotes', type: 'list' });

    const stopPropagation = (e: any) => e.stopPropagation();

    const approveQuote = () => {
        setPendingStatus('approved');
    };

    const deniedQuote = () => {
        setPendingStatus('rejected');
    };

    const updateQuoteStatus = (status: number) => {
        setQuoteAccepting(true);
        update(
            'quotes/status',
            {
                id: record.id,
                data: { status },
                previousData: record,
            },
            {
                onSuccess: () => {
                    setPendingStatus(null);
                    setTimeout(() => {
                        setQuoteAccepting(false);
                        refresh();
                    }, 1000);
                },
                onError: (error) => {
                    setQuoteAccepting(false);
                    setPendingStatus(null);
                    notify(quoteStatusErrorMessage(error), { type: 'error' });
                },
            }
        )
    };

    const confirmPendingStatus = () => {
        if (!pendingStatus) return;
        const status =
            pendingStatus === 'approved'
                ? quoteStatuses.approved
                : quoteStatuses.rejected;
        updateQuoteStatus(status);
    };

    const openPriceEdit = () => {
        setPriceEditValue(String(record.total_price ?? record.price ?? ''));
        setPriceReason('');
        setPriceEditError('');
        setPriceEditOpen(true);
    };

    const confirmPriceEdit = () => {
        const cleanedReason = priceReason.trim();
        if (
            cleanedReason.length < PRICE_CHANGE_REASON_MIN
            || cleanedReason.length > PRICE_CHANGE_REASON_MAX
        ) {
            setPriceEditError(
                `Reason must be ${PRICE_CHANGE_REASON_MIN}–${PRICE_CHANGE_REASON_MAX} characters.`,
            );
            return;
        }
        const parsed = Number(priceEditValue);
        if (!Number.isFinite(parsed) || parsed < 0) {
            setPriceEditError('Enter a valid total price.');
            return;
        }
        setSavingPrice(true);
        update(
            'quotes/price',
            {
                id: record.id,
                data: {
                    total_price: parsed,
                    price_change_reason: cleanedReason,
                },
                previousData: record,
            },
            {
                onSuccess: () => {
                    setSavingPrice(false);
                    setPriceEditOpen(false);
                    notify('Quote price updated', { type: 'success' });
                    refresh();
                },
                onError: (error) => {
                    setSavingPrice(false);
                    notify(priceUpdateErrorMessage(error), { type: 'error' });
                },
            },
        );
    };

    const runRegenerateEstimate = async (confirmVoidSigning: boolean) => {
        setRegenerating(true);
        try {
            const res = await authedFetch(
                `/quotes/${record.id}/regenerate-estimate`
                    + (confirmVoidSigning ? '?confirm_void_signing=true' : ''),
                { method: 'POST' }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (isContractSigningActiveError(res.status, data)) {
                    // Active DocuSeal signing: ask before voiding it. Retried with confirm_void_signing=true.
                    setVoidSigningConfirmOpen(true);
                    return;
                }
                const err = data?.detail ?? data?.errors;
                let msg = 'Failed to regenerate estimate';
                if (typeof err === 'string') msg = err;
                else if (err?.root) msg = String(err.root);
                notify(msg, { type: 'error' });
                return;
            }
            notify('Estimate PDF regenerated', { type: 'success' });
            if (data?.contract_signing_reissued === false) {
                notify(
                    "Signing was voided but could not be re-sent — start it again from the project's Contract tab.",
                    { type: 'warning' },
                );
            }
            refresh();
        } catch {
            notify('Failed to regenerate estimate', { type: 'error' });
        } finally {
            setRegenerating(false);
        }
    };

    const handleRegenerateEstimate = () => runRegenerateEstimate(false);

    const handlePreviewEstimatePdf = () => {
        const token = getAccessToken();
        if (!token) {
            notify('Not signed in', { type: 'error' });
            return;
        }
        const base = `${import.meta.env.VITE_APP_API_BACKEND}/quotes/${record.id}/preview-estimate`;
        if (import.meta.env.DEV) {
            const url = `${base}?access_token=${encodeURIComponent(token)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        authedFetch(`/quotes/${record.id}/preview-estimate`)
            .then(async res => {
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    const err = data?.detail ?? data?.errors;
                    let msg = 'Preview failed';
                    if (typeof err === 'string') msg = err;
                    else if (err?.root) msg = String(err.root);
                    throw new Error(msg);
                }
                return res.blob();
            })
            .then(blob => {
                window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
            })
            .catch((e: Error) => {
                notify(e.message || 'Preview failed', { type: 'error' });
            });
    };

    const handleMarkEstimateSent = async () => {
        setMarkingSent(true);
        try {
            await markQuoteEstimateSent(record.id as number);
            notify('Estimate marked as sent', { type: 'success' });
            refresh();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to mark estimate as sent', {
                type: 'error',
            });
        } finally {
            setMarkingSent(false);
        }
    };

    return (
        <Box sx={showAsideSx}>
            <Confirm
                isOpen={voidSigningConfirmOpen}
                title="Contract signing in progress"
                content={CONTRACT_SIGNING_VOID_MESSAGE}
                confirm="Continue"
                onConfirm={() => {
                    setVoidSigningConfirmOpen(false);
                    void runRegenerateEstimate(true);
                }}
                onClose={() => setVoidSigningConfirmOpen(false)}
            />
            <Dialog
                open={pendingStatus !== null}
                onClose={() => !isQuoteAccepting && setPendingStatus(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {pendingStatus === 'approved' ? 'Approve quote?' : 'Deny quote?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingStatus === 'approved'
                            ? 'This will mark the quote as approved, reject the other quotes on this project, and move the job folder files into the 1. Projects folder on SharePoint (other Client Estimate PDFs in that folder are removed).'
                            : 'This will mark the quote as denied. This action cannot be undone from the approve/deny buttons.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={() => setPendingStatus(null)}
                        disabled={isQuoteAccepting}
                    >
                        Cancel
                    </MuiButton>
                    <MuiButton
                        onClick={confirmPendingStatus}
                        color={pendingStatus === 'approved' ? 'success' : 'error'}
                        variant="contained"
                        disabled={isQuoteAccepting}
                        autoFocus
                    >
                        Confirm
                    </MuiButton>
                </DialogActions>
            </Dialog>
            <Dialog
                open={priceEditOpen}
                onClose={() => !isSavingPrice && setPriceEditOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Edit total price</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Current total price is{' '}
                        <strong>
                            {money(record.total_price ?? record.price ?? 0)}
                        </strong>
                        . Enter the new price and a short reason (3–32 characters).
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="New total price"
                        type="number"
                        value={priceEditValue}
                        onChange={(e) => {
                            setPriceEditValue(e.target.value);
                            if (priceEditError) setPriceEditError('');
                        }}
                        disabled={isSavingPrice}
                        sx={{ mb: 2 }}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                        fullWidth
                        label="Reason"
                        value={priceReason}
                        onChange={(e) => {
                            setPriceReason(e.target.value);
                            if (priceEditError) setPriceEditError('');
                        }}
                        error={!!priceEditError}
                        helperText={priceEditError || '3–32 characters'}
                        disabled={isSavingPrice}
                        inputProps={{ maxLength: PRICE_CHANGE_REASON_MAX }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                confirmPriceEdit();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={() => setPriceEditOpen(false)}
                        disabled={isSavingPrice}
                    >
                        Cancel
                    </MuiButton>
                    <MuiButton
                        variant="contained"
                        onClick={confirmPriceEdit}
                        disabled={isSavingPrice}
                    >
                        Save price
                    </MuiButton>
                </DialogActions>
            </Dialog>
            <SendEstimateEmailDialog
                open={sendEmailOpen}
                onClose={() => setSendEmailOpen(false)}
                quoteId={Number(record.id)}
                onSent={() => refresh()}
            />
            <Dialog
                open={selectedPriceEntry != null}
                onClose={() => setSelectedPriceEntry(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Price change</DialogTitle>
                <DialogContent>
                    {selectedPriceEntry && (
                        <>
                            <DialogContentText sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                <strong>Reason:</strong> {selectedPriceEntry.reason}
                            </DialogContentText>
                            <DialogContentText sx={{ mb: 1 }}>
                                <strong>Date:</strong> {formatDate(selectedPriceEntry.at)}
                            </DialogContentText>
                            <DialogContentText sx={{ mb: 2 }}>
                                <strong>Created by:</strong> {selectedPriceEntry.user_name}
                            </DialogContentText>

                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Changes:
                            </Typography>

                            {Object.keys(selectedPriceEntry.new_value || {}).map(key => (
                                <DialogContentText
                                    key={key}
                                    sx={{ mb: 1, wordBreak: 'break-word' }}
                                >
                                    <strong>{key}:</strong>{' '}
                                    {selectedPriceEntry.old_value?.[key] ?? ''} →{' '}
                                    {selectedPriceEntry.new_value?.[key] ?? ''}
                                </DialogContentText>
                            ))}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={() => setSelectedPriceEntry(null)}>Close</MuiButton>
                </DialogActions>
            </Dialog>
            {link !== 'edit' ? (
                <></>
            ) : (
                <>
                    <Box my={2}>
                        <Button
                            component={Link}
                            to={backTo}
                            label={'Back'}
                            onClick={stopPropagation}>
                            <ArrowBackIcon />
                        </Button>
                    </Box>
                    <Divider />
                </>
            )}
            <Box my={2}>
                {record.status !== quoteStatuses.rejected && !isLegacy && (
                    <>
                        {link === 'edit' ? (
                            <EditButton label="Edit Estimate" />
                        ) : (
                            <ShowButton label="Show Quote" />
                        )}
                    </>
                )}
                {isLegacy ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {projectPhase === 'quoting'
                            ? 'Legacy quote — approve/deny and price edit enabled'
                            : 'Legacy quote — price edit enabled'}
                    </Typography>
                ) : (
                    <>
                        <br/>
                        <DeleteButton mutationMode="pessimistic" redirect={location.state?.from || undefined} />
                    </>
                )}
            </Box>
            {link === 'edit' && (
                <>
                    {!isLegacy && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box my={2}>
                                {hasEstimatorDocument && (
                                    <Box mb={2}>
                                        <Box mb={1}>
                                            <Button
                                                label="Preview PDF"
                                                onClick={handlePreviewEstimatePdf}>
                                                <VisibilityIcon />
                                            </Button>
                                        </Box>
                                        <Box mb={1}>
                                            <Button
                                                label="Send to client"
                                                onClick={() => setSendEmailOpen(true)}>
                                                <EmailIcon />
                                            </Button>
                                            {estimateSentAt && (
                                                <Typography variant="body2" color="textSecondary">
                                                    Sent {formatDate(estimateSentAt)}
                                                </Typography>
                                            )}
                                            {isSuperuser && !estimateSentAt && (
                                                <Box mt={1}>
                                                    <Button
                                                        label={
                                                            isMarkingSent
                                                                ? 'Marking…'
                                                                : 'Mark as sent'
                                                        }
                                                        onClick={handleMarkEstimateSent}
                                                        disabled={isMarkingSent}
                                                    >
                                                        <DoneAllIcon />
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box position="relative">
                                        <Button
                                            label={isRegenerating ? 'Regenerating…' : 'Regenerate estimate'}
                                            color="success"
                                            onClick={handleRegenerateEstimate}
                                            disabled={isRegenerating}>
                                            <PictureAsPdfIcon />
                                        </Button>
                                        {isRegenerating && (
                                            <CircularProgress
                                                size={24}
                                                sx={{
                                                    color: '#4caf50',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    marginTop: '-12px',
                                                    marginLeft: '-12px',
                                                }}
                                            />
                                        )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                    {isLegacy && (
                        <Box my={2}>
                            <Button label="Edit price" onClick={openPriceEdit}>
                                <EditIcon />
                            </Button>
                        </Box>
                    )}
                    <Box my={2} position="relative">
                        {canApproveDeny && (
                            <>
                                <ButtonGroup variant="contained" aria-label="Basic button group">
                                    <Button
                                        label="Approve"
                                        color="success"
                                        onClick={approveQuote}
                                        disabled={isQuoteAccepting}>
                                        <CheckIcon />
                                    </Button>
                                    <Button
                                        label="Denied"
                                        color="error"
                                        onClick={deniedQuote}
                                        disabled={isQuoteAccepting}>
                                        <CloseIcon />
                                    </Button>
                                </ButtonGroup>
                                {isQuoteAccepting && (
                                    <CircularProgress size={24}
                                    sx={{
                                        color: '#4caf50',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                    }} />
                                )}
                            </>
                        )}
                        {record.status === quoteStatuses.approved && (
                            <Typography variant="body2" color="success">Approved at {formatDate(record.approved_at)}</Typography>
                        )}
                        {record.status === quoteStatuses.rejected && (
                            <Typography variant="body2" color="error">Rejected at {formatDate(record.rejected_at)}</Typography>
                        )}
                    </Box>
                    {priceHistoryNewestFirst.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box mb={2}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Price history
                                </Typography>
                                <Stack spacing={0.75}>
                                    {priceHistoryNewestFirst.slice(0, 5).map((entry, idx) => (
                                        <Box
                                            key={`${entry.at}-${entry.reason}-${idx}`}
                                            onClick={() => setSelectedPriceEntry(entry)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.75,
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                noWrap
                                                title={entry.reason}
                                            >
                                                {entry.reason}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontFamily="monospace"
                                                fontWeight={600}
                                                noWrap
                                            >
                                                {money(entry.new_price)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                noWrap
                                                title={`${entry.user_name} · ${formatDate(entry.at)}`}
                                            >
                                                {entry.user_name} · {formatDate(entry.at)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};
